import React, { useMemo, useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MoreHorizontal, ArrowUpDown, ArrowUp, ArrowDown, Trash2, Package, DollarSign, ChevronLeft, ChevronRight, Plus, FileSpreadsheet, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Transaction } from "../types";
import { formatDate } from "../lib/utils";
import { getSignedImageUrls } from "../services/cardImageService";

interface InventoryTableProps {
  transactions: Transaction[];
  onEditTransaction?: (transaction: Transaction) => void;
  onDeleteTransaction?: (id: string) => void;
  onMoveToGrading?: (transaction: Transaction) => void;
  onMarkAsSold?: (transaction: Transaction) => void;
  canMoveToGrading?: (transaction: Transaction) => boolean;
  isSoldTable?: boolean;
  selectedItems?: Set<string>;
  onSelectItem?: (id: string) => void;
  onSelectAll?: () => void;
  selectAll?: boolean;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  visibleColumns?: string[];
  columnOrder?: string[];
  cardImagePreviewBehavior?: 'hover' | 'click';
  cardImagePreviewSize?: 'small' | 'medium' | 'large';
  // Empty state actions (only used for active inventory, not sold table)
  onAddNewCard?: () => void;
}

const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(amount);
};

const calculateDaysInInventory = (purchaseDate: string | undefined) => {
  if (!purchaseDate) return 0;
  try {
    const purchase = new Date(purchaseDate);
    const today = new Date();
    const diffTime = Math.abs(today.getTime() - purchase.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch {
    return 0;
  }
};

// Helper component for sortable headers
const SortableHeader = ({ field, label, onSort, sortField, sortDirection }: { field: string, label: string, onSort?: (field: string) => void, sortField?: string, sortDirection?: 'asc' | 'desc' }) => {
  const isSorted = sortField === field;
  const Icon = () => {
    if (!isSorted) return <ArrowUpDown className="h-3 w-3" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
  };
  
  return (
    <Button 
      variant="ghost" 
      size="sm" 
      className="gap-1 hover:bg-muted"
      onClick={() => onSort?.(field)}
    >
      {label}
      <Icon />
    </Button>
  );
};

export const InventoryTable = ({ 
  transactions, 
  onEditTransaction, 
  onDeleteTransaction,
  onMoveToGrading,
  onMarkAsSold,
  isSoldTable = false,
  selectedItems = new Set(),
  onSelectItem,
  onSelectAll,
  selectAll = false,
  sortField,
  sortDirection,
  onSort,
  visibleColumns,
  columnOrder,
  canMoveToGrading,
  cardImagePreviewBehavior = 'hover',
  cardImagePreviewSize = 'small',
  onAddNewCard,
}: InventoryTableProps) => {
  const navigate = useNavigate();
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  const CardNameCell = ({ transaction, cardName, count }: { transaction: Transaction; cardName: string; count?: number }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const imageItems = useMemo(() => {
      const items: Array<{ label: string; path: string }> = [];
      if (transaction.front_image_path) {
        items.push({ label: "Front", path: transaction.front_image_path });
      }
      if (transaction.back_image_path) {
        items.push({ label: "Back", path: transaction.back_image_path });
      }
      const others = Array.isArray(transaction.other_image_paths) ? transaction.other_image_paths.slice(0, 3) : [];
      others.forEach((path, index) => {
        if (path) {
          items.push({ label: `Other ${index + 1}`, path });
        }
      });
      return items;
    }, [transaction]);
    const hasImages = imageItems.length > 0;
    const [isHovered, setIsHovered] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [signedItems, setSignedItems] = useState<Array<{ label: string; url: string }>>([]);
    const [bubbleStyle, setBubbleStyle] = useState<React.CSSProperties | null>(null);
    const bubbleRef = useRef<HTMLDivElement | null>(null);
    const shouldShow = cardImagePreviewBehavior === "hover" && isHovered;
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const shouldLoadImages = shouldShow || isGalleryOpen;

    const sizeMap = {
      small: { width: "w-[28rem]", image: "h-32", columns: "grid-cols-2" },
      medium: { width: "w-[40rem]", image: "h-48", columns: "grid-cols-2" },
      large: { width: "w-[46rem]", image: "h-56", columns: "grid-cols-2" },
    };
    const sizeConfig = sizeMap[cardImagePreviewSize];

    useEffect(() => {
      if (!shouldLoadImages || !hasImages) {
        return;
      }
      let isMounted = true;
      setIsLoading(true);
      getSignedImageUrls(imageItems.map((item) => item.path))
        .then((urls) => {
          if (!isMounted) return;
          const nextItems = imageItems.map((item, index) => ({
            label: item.label,
            url: urls[index],
          }));
          setSignedItems(nextItems);
        })
        .catch(() => {
          if (isMounted) {
            setSignedItems([]);
          }
        })
        .finally(() => {
          if (isMounted) {
            setIsLoading(false);
          }
        });
      return () => {
        isMounted = false;
      };
    }, [imageItems, shouldLoadImages, hasImages]);

    useLayoutEffect(() => {
      if (!shouldShow || !hasImages) {
        return;
      }
      const updatePosition = () => {
        const trigger = containerRef.current;
        const bubble = bubbleRef.current;
        if (!trigger || !bubble) {
          return;
        }
        const triggerRect = trigger.getBoundingClientRect();
        const bubbleRect = bubble.getBoundingClientRect();
        const padding = 12;
        let left = triggerRect.left;
        if (left + bubbleRect.width > window.innerWidth - padding) {
          left = window.innerWidth - padding - bubbleRect.width;
        }
        if (left < padding) {
          left = padding;
        }
        let top = triggerRect.bottom + 8;
        if (top + bubbleRect.height > window.innerHeight - padding) {
          const above = triggerRect.top - bubbleRect.height - 8;
          if (above >= padding) {
            top = above;
          }
        }
        setBubbleStyle({ position: "fixed", left, top });
      };
      const frame = requestAnimationFrame(updatePosition);
      const handleScroll = () => updatePosition();
      window.addEventListener("scroll", handleScroll, true);
      window.addEventListener("resize", handleScroll);
      return () => {
        cancelAnimationFrame(frame);
        window.removeEventListener("scroll", handleScroll, true);
        window.removeEventListener("resize", handleScroll);
      };
    }, [shouldShow, hasImages, signedItems, isLoading]);

    const handleTrigger = () => {
      if (!hasImages) return;
      setActiveIndex(0);
      setIsGalleryOpen(true);
    };

    const handleCloseGallery = () => {
      setIsGalleryOpen(false);
      setActiveIndex(0);
    };

    return (
      <div
        ref={containerRef}
        className="relative"
        onMouseEnter={cardImagePreviewBehavior === "hover" && hasImages ? () => setIsHovered(true) : undefined}
        onMouseLeave={cardImagePreviewBehavior === "hover" && hasImages ? () => setIsHovered(false) : undefined}
      >
        <div
          className={`truncate ${hasImages ? "cursor-pointer" : ""}`}
          onClick={hasImages ? handleTrigger : undefined}
        >
          {cardName}
          {count && count > 1 && (
            <span className="text-muted-foreground font-normal"> (x{count})</span>
          )}
        </div>
        {hasImages && shouldShow && createPortal(
          <div
            ref={bubbleRef}
            style={bubbleStyle || { position: "fixed", left: -9999, top: -9999 }}
            className={`z-50 ${sizeConfig.width} rounded-lg border border-border bg-background p-3 shadow-lg`}
            onMouseEnter={cardImagePreviewBehavior === "hover" ? () => setIsHovered(true) : undefined}
            onMouseLeave={cardImagePreviewBehavior === "hover" ? () => setIsHovered(false) : undefined}
          >
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading images...</div>
            ) : (
              <div className={`grid ${sizeConfig.columns} gap-2`}>
                {signedItems.map((item) => (
                  <div key={item.label} className="space-y-1">
                    <div className="text-xs text-muted-foreground">{item.label}</div>
                    <img
                      src={item.url}
                      alt={item.label}
                      className={`${sizeConfig.image} w-full rounded-md object-cover`}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>,
          document.body
        )}
        {hasImages && isGalleryOpen && createPortal(
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center bg-background/95 backdrop-blur"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="absolute right-6 top-6 rounded-md border border-border bg-background px-3 py-1 text-sm"
              onClick={handleCloseGallery}
            >
              Close
            </button>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">Loading images...</div>
            ) : (
              <div className="flex w-full max-w-5xl flex-col items-center gap-4 px-6">
                <div className="text-sm text-muted-foreground">
                  {signedItems[activeIndex]?.label || "Photo"}
                </div>
                <div className="flex w-full items-center gap-4">
                  <button
                    type="button"
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                    onClick={() =>
                      setActiveIndex((prev) =>
                        signedItems.length === 0 ? 0 : (prev - 1 + signedItems.length) % signedItems.length
                      )
                    }
                  >
                    Prev
                  </button>
                  <div className="flex-1">
                    {signedItems[activeIndex] && (
                      <img
                        src={signedItems[activeIndex].url}
                        alt={signedItems[activeIndex].label}
                        className="max-h-[70vh] w-full rounded-lg object-contain"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    className="rounded-md border border-border bg-background px-3 py-2 text-sm"
                    onClick={() =>
                      setActiveIndex((prev) =>
                        signedItems.length === 0 ? 0 : (prev + 1) % signedItems.length
                      )
                    }
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>,
          document.body
        )}
      </div>
    );
  };
  
  // Helper to check if a column should be visible
  const isColumnVisible = (columnKey: string) => {
    if (!visibleColumns || visibleColumns.length === 0) return true;
    return visibleColumns.includes(columnKey);
  };
  
  // Get ordered list of visible columns
  const getOrderedVisibleColumns = () => {
    const defaultOrder = isSoldTable 
      ? ['card', 'sport', 'purchase_date', 'purchase_platform', 'cost', 'totalCost', 'daysInInventory', 
         'grade', 'year', 'manufacturer', 'set', 'card_number', 'subject', 'league', 'insert_name', 'parallel', 'numbering',
         'bought_from', 'other', 
         'sale_date', 'sale_price', 'sale_platform', 'sold_to', 'marketplace_fees', 'shipping_fees', 'netProfit', 'notes']
      : ['card', 'sport', 'purchase_date', 'purchase_platform', 'cost', 'totalCost', 'daysInInventory',
         'grade', 'year', 'manufacturer', 'set', 'card_number', 'subject', 'league', 'insert_name', 'parallel', 'numbering',
         'bought_from', 'other', 'notes'];
    const baseOrder = !columnOrder || columnOrder.length === 0 ? defaultOrder : columnOrder;
    const merged: string[] = [...baseOrder];
    defaultOrder.forEach((key) => {
      if (!merged.includes(key)) merged.push(key);
    });
    return merged.filter(col => isColumnVisible(col));
  };
  
  // Helper to render column header
  const renderColumnHeader = (columnKey: string) => {
    const headerMap: Record<string, { label: string, sortable?: boolean }> = {
      card: { label: 'Card', sortable: true },
      sport: { label: 'Sport/TCG', sortable: true },
      purchase_date: { label: 'Purchase Date', sortable: true },
      purchase_platform: { label: isSoldTable ? 'Purchase Platform' : 'Platform', sortable: false },
      cost: { label: isSoldTable ? 'Purchase Price' : 'Price', sortable: true },
      totalCost: { label: 'Total Cost', sortable: false },
      daysInInventory: { label: 'Days in Inventory', sortable: false },
      grade: { label: 'Grade', sortable: true },
      year: { label: 'Year', sortable: false },
      manufacturer: { label: 'Manufacturer', sortable: false },
      set: { label: 'Set', sortable: false },
      card_number: { label: 'Card #', sortable: false },
      subject: { label: 'Player / Character', sortable: false },
      league: { label: 'League', sortable: false },
      insert_name: { label: 'Insert / Subset', sortable: false },
      parallel: { label: 'Parallel', sortable: false },
      numbering: { label: 'Print Run', sortable: false },
      bought_from: { label: 'Bought From', sortable: false },
      other: { label: 'Other Info', sortable: false },
      notes: { label: 'Notes', sortable: false },
      sale_date: { label: 'Sale Date', sortable: false },
      sale_price: { label: 'Sale Price', sortable: false },
      sale_platform: { label: 'Sale Platform', sortable: false },
      sold_to: { label: 'Sold To', sortable: false },
      marketplace_fees: { label: 'Marketplace Fees', sortable: false },
      shipping_fees: { label: 'Shipping Fees', sortable: false },
      netProfit: { label: 'Net Profit', sortable: false },
    };
    
    const header = headerMap[columnKey];
    if (!header) return null;
    
    return (
      <TableHead key={columnKey} className="px-2 sm:px-4">
        {onSort && header.sortable ? (
          <SortableHeader field={columnKey} label={header.label} onSort={onSort} sortField={sortField} sortDirection={sortDirection} />
        ) : (
          header.label
        )}
      </TableHead>
    );
  };
  
  // Helper to render cell content
  const renderCellContent = (columnKey: string, transaction: Transaction, totalCost: number) => {
    const sport = transaction.sport || 'Unknown';
    const daysInInventory = calculateDaysInInventory(transaction.purchase_date);
    
    switch (columnKey) {
      case 'card':
        const count = (transaction as any)._count;
        const cardName = transaction.card || 'Unknown Card';
        return (
          <TableCell key={columnKey} className="font-medium max-w-xs px-2 sm:px-4">
            <CardNameCell transaction={transaction} cardName={cardName} count={count} />
          </TableCell>
        );
      case 'sport':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{sport}</Badge>
          </TableCell>
        );
      case 'purchase_date':
        return (
          <TableCell key={columnKey} className="text-muted-foreground px-2 sm:px-4">
            {formatDate(transaction.purchase_date)}
          </TableCell>
        );
      case 'purchase_platform':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.purchase_platform || 'Unknown'}</Badge>
          </TableCell>
        );
      case 'cost':
        const costCount = (transaction as any)._count;
        const cost = typeof transaction.cost === 'number' ? transaction.cost : parseFloat(transaction.cost || '0');
        return (
          <TableCell key={columnKey} className="font-medium px-2 sm:px-4">
            {formatCurrency(cost)}
            {costCount && costCount > 1 && (
              <span className="text-muted-foreground text-xs ml-1">/ea</span>
            )}
          </TableCell>
        );
      case 'totalCost':
        const totalCount = (transaction as any)._count;
        return (
          <TableCell key={columnKey} className="font-medium px-2 sm:px-4">
            {formatCurrency(totalCost)}
            {totalCount && totalCount > 1 && (
              <span className="text-muted-foreground text-xs ml-1">/ea</span>
            )}
          </TableCell>
        );
      case 'daysInInventory':
        return (
          <TableCell key={columnKey} className="text-muted-foreground px-2 sm:px-4">
            {daysInInventory}
          </TableCell>
        );
      case 'grade':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.grade || 'Ungraded'}</Badge>
          </TableCell>
        );
      case 'year':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.year || 'N/A'}</Badge>
          </TableCell>
        );
      case 'manufacturer':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.manufacturer || 'N/A'}</Badge>
          </TableCell>
        );
      case 'set':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.set || 'N/A'}</Badge>
          </TableCell>
        );
      case 'card_number':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.card_number || 'N/A'}</Badge>
          </TableCell>
        );
      case 'subject':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4 max-w-[10rem]">
            <Badge variant="outline" className="bg-primary/20 border-primary truncate block">{transaction.subject || 'N/A'}</Badge>
          </TableCell>
        );
      case 'league':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.league || 'N/A'}</Badge>
          </TableCell>
        );
      case 'insert_name':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4 max-w-xs">
            <Badge variant="outline" className="bg-primary/20 border-primary truncate block">{transaction.insert_name || 'N/A'}</Badge>
          </TableCell>
        );
      case 'parallel':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4 max-w-xs">
            <Badge variant="outline" className="bg-primary/20 border-primary truncate block">{transaction.parallel || 'N/A'}</Badge>
          </TableCell>
        );
      case 'numbering':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4 max-w-xs">
            <Badge variant="outline" className="bg-primary/20 border-primary truncate block">{transaction.numbering || 'N/A'}</Badge>
          </TableCell>
        );
      case 'bought_from':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.bought_from || 'N/A'}</Badge>
          </TableCell>
        );
      case 'other':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4 max-w-xs">
            <Badge variant="outline" className="bg-primary/20 border-primary truncate block">{transaction.other || 'N/A'}</Badge>
          </TableCell>
        );
      case 'notes':
        return (
          <TableCell key={columnKey} className="text-muted-foreground px-2 sm:px-4 max-w-xs">
            <div className="truncate">{transaction.notes || 'N/A'}</div>
          </TableCell>
        );
      case 'sale_date':
        return (
          <TableCell key={columnKey} className="text-muted-foreground px-2 sm:px-4">
            {formatDate(transaction.sale_date)}
          </TableCell>
        );
      case 'sale_price':
        return (
          <TableCell key={columnKey} className="font-medium px-2 sm:px-4">
            {formatCurrency(typeof transaction.sale_price === 'number' ? transaction.sale_price : parseFloat(transaction.sale_price || '0'))}
          </TableCell>
        );
      case 'sale_platform':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.sale_platform || 'N/A'}</Badge>
          </TableCell>
        );
      case 'sold_to':
        return (
          <TableCell key={columnKey} className="px-2 sm:px-4">
            <Badge variant="outline" className="bg-primary/20 border-primary">{transaction.sold_to || 'N/A'}</Badge>
          </TableCell>
        );
      case 'marketplace_fees':
        return (
          <TableCell key={columnKey} className="text-muted-foreground px-2 sm:px-4">
            {formatCurrency(typeof transaction.marketplace_fees === 'number' ? transaction.marketplace_fees : parseFloat(transaction.marketplace_fees || '0'))}
          </TableCell>
        );
      case 'shipping_fees':
        return (
          <TableCell key={columnKey} className="text-muted-foreground px-2 sm:px-4">
            {formatCurrency(typeof transaction.shipping_fees === 'number' ? transaction.shipping_fees : parseFloat(transaction.shipping_fees || '0'))}
          </TableCell>
        );
      case 'netProfit':
        const salePrice = typeof transaction.sale_price === 'number' ? transaction.sale_price : parseFloat(transaction.sale_price || '0');
        const marketplaceFees = typeof transaction.marketplace_fees === 'number' ? transaction.marketplace_fees : parseFloat(transaction.marketplace_fees || '0');
        const shippingFees = typeof transaction.shipping_fees === 'number' ? transaction.shipping_fees : parseFloat(transaction.shipping_fees || '0');
        const profit = salePrice - totalCost - marketplaceFees - shippingFees;
        return (
          <TableCell key={columnKey} className={`font-medium px-2 sm:px-4 ${profit >= 0 ? 'text-success' : 'text-destructive'}`}>
            {formatCurrency(profit)}
          </TableCell>
        );
      default:
        return null;
    }
  };

  // Calculate total visible columns for colspan
  const visibleColumnCount = () => {
    const orderedColumns = getOrderedVisibleColumns();
    return orderedColumns.length + 2; // +1 for checkbox, +1 for actions
  };
  
  // Calculate pagination
  const totalPages = Math.ceil(transactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = transactions.slice(startIndex, endIndex);
  
  // Reset to page 1 when transactions change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [transactions.length]);
  
  const orderedColumns = getOrderedVisibleColumns();
  
  return (
    <div className="space-y-4">
      <div className="glass-card rounded-xl overflow-hidden animate-fade-in-up">
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <Table className="min-w-full">
          <TableHeader>
            <TableRow className="bg-muted/50">
              {onSelectAll && (
                <TableHead className="sticky left-0 bg-background z-10 shadow-[inset_-1px_0_0_0_hsl(var(--border))] p-0">
                  <div className="pl-4 pr-4 py-3.5">
                    <Checkbox 
                      checked={selectAll}
                      onCheckedChange={onSelectAll}
                    />
                  </div>
                </TableHead>
              )}
              {orderedColumns.map(col => renderColumnHeader(col))}
              <TableHead className="sticky right-0 bg-background shadow-[inset_1px_0_0_0_hsl(var(--border))] p-0 min-w-[64px]">
                <div className="pl-4 pr-4 py-3.5"></div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {transactions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnCount()} className="p-0">
                  {isSoldTable ? (
                    <div className="text-center py-12 text-muted-foreground">
                      No sold cards found.
                    </div>
                  ) : (
                    <div className="py-16 px-4">
                      <div className="max-w-md mx-auto text-center space-y-6">
                        {/* Decorative icon */}
                        <div className="relative mx-auto w-24 h-24">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 rounded-3xl rotate-6" />
                          <div className="absolute inset-0 bg-gradient-to-br from-primary/30 to-primary/10 rounded-3xl -rotate-3" />
                          <div className="relative bg-gradient-to-br from-primary/40 to-primary/20 rounded-3xl w-full h-full flex items-center justify-center">
                            <Package className="h-10 w-10 text-primary" />
                          </div>
                          <div className="absolute -top-1 -right-1">
                            <Sparkles className="h-5 w-5 text-yellow-500" />
                          </div>
                        </div>
                        
                        {/* Text content */}
                        <div className="space-y-2">
                          <h3 className="text-xl font-semibold text-foreground">
                            Your inventory is empty
                          </h3>
                          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
                            Start tracking your card collection by adding your first card or importing from a spreadsheet.
                          </p>
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                          {onAddNewCard && (
                            <Button 
                              variant="gradient" 
                              size="lg"
                              className="gap-2"
                              onClick={onAddNewCard}
                            >
                              <Plus className="h-4 w-4" />
                              Add Your First Card
                            </Button>
                          )}
                          <Button 
                            variant="outline" 
                            size="lg"
                            className="gap-2"
                            onClick={() => navigate('/settings')}
                          >
                            <FileSpreadsheet className="h-4 w-4" />
                            Import from Spreadsheet
                          </Button>
                        </div>
                        
                        {/* Helpful tip */}
                        <p className="text-xs text-muted-foreground/70 pt-4">
                          💡 Tip: You can import your existing collection from a CSV or Excel file
                        </p>
                      </div>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ) : (
              paginatedTransactions.map((transaction, index) => {
                const totalCost = (typeof transaction.cost === 'number' ? transaction.cost : parseFloat(transaction.cost || '0')) + (typeof transaction.grading_cost === 'number' ? transaction.grading_cost : parseFloat(transaction.grading_cost || '0'));
                const canMoveThisTransaction = canMoveToGrading ? canMoveToGrading(transaction) : true;
                
                return (
                  <TableRow
                    key={transaction.id}
                    className="transition-colors hover:bg-muted/50 animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {onSelectItem && (
                      <TableCell className="sticky left-0 bg-background z-10 shadow-[inset_-1px_0_0_0_hsl(var(--border))] p-0">
                        <div className="pl-4 pr-4 py-4">
                          <Checkbox 
                            checked={selectedItems.has(transaction.id || '')}
                            onCheckedChange={() => transaction.id && onSelectItem(transaction.id)}
                          />
                        </div>
                      </TableCell>
                    )}
                    {orderedColumns.map(col => renderCellContent(col, transaction, totalCost))}
                    <TableCell className="sticky right-0 bg-background shadow-[inset_1px_0_0_0_hsl(var(--border))] p-0 min-w-[64px]">
                      <div className="pl-4 pr-4 py-4">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!isSoldTable && (
                            <>
                              {onMoveToGrading && (
                                <DropdownMenuItem 
                                  onClick={() => canMoveThisTransaction && onMoveToGrading(transaction)}
                                  className="flex items-center gap-2"
                                  disabled={!canMoveThisTransaction}
                                >
                                  <Package className="h-4 w-4" />
                                  Move to Grading
                                </DropdownMenuItem>
                              )}
                              {onMarkAsSold && (
                                <DropdownMenuItem 
                                  onClick={() => onMarkAsSold(transaction)}
                                  className="flex items-center gap-2"
                                >
                                  <DollarSign className="h-4 w-4" />
                                  Mark as Sold
                                </DropdownMenuItem>
                              )}
                              {(onMoveToGrading || onMarkAsSold) && <DropdownMenuSeparator />}
                            </>
                          )}
                          {onEditTransaction && (
                            <DropdownMenuItem 
                              onClick={() => onEditTransaction(transaction)}
                              className="flex items-center gap-2"
                            >
                              Edit
                            </DropdownMenuItem>
                          )}
                          {onDeleteTransaction && transaction.id && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => transaction.id && onDeleteTransaction(transaction.id)}
                                className="flex items-center gap-2 text-destructive focus:text-destructive"
                              >
                                <Trash2 className="h-4 w-4" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        </div>
      </div>
      
      {/* Pagination Controls */}
      {transactions.length > itemsPerPage && (
        <div className="flex items-center justify-between px-4 py-3">
          <div className="text-sm text-muted-foreground">
            Showing {startIndex + 1} to {Math.min(endIndex, transactions.length)} of {transactions.length} items
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </Button>
            <div className="text-sm text-muted-foreground">
              Page {currentPage} of {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};